/**
 * DroneControls.jsx
 * =================
 * Bottom control panel for commanding the ESP32-CAM drone.
 *
 * Controls:
 *  - Start / Stop capture buttons
 *  - Capture interval dropdown (2, 3, 5, 10, 30 s)
 *  - Online / Offline mode toggle
 *  - Last command status indicator
 *
 * Each action sends POST /api/drone/command → Flask → MQTT → ESP32-CAM
 */

import React, { useState, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const INTERVALS = [
  { label: '2 s',  value: 2  },
  { label: '3 s',  value: 3  },
  { label: '5 s',  value: 5  },
  { label: '10 s', value: 10 },
  { label: '30 s', value: 30 },
];

export default function DroneControls({ onCommandSent }) {
  const [capturing,  setCapturing]  = useState(false);
  const [interval,   setIntervalVal]= useState(3);
  const [mode,       setMode]       = useState('online');  // 'online' | 'offline'
  const [lastCmd,    setLastCmd]    = useState(null);
  const [cmdStatus,  setCmdStatus]  = useState(null);  // 'ok' | 'fail' | null
  const [loading,    setLoading]    = useState(false);

  const sendCommand = useCallback(async (command) => {
    setLoading(true);
    setCmdStatus(null);
    try {
      const resp = await fetch(`${API_BASE}/api/drone/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command }),
      });
      const data = await resp.json();
      if (resp.ok && data.sent !== false) {
        setCmdStatus('ok');
        setLastCmd({ command, demo: data.demo_mode });
      } else {
        setCmdStatus('fail');
        setLastCmd({ command, error: data.error || 'Unknown error' });
      }
    } catch (e) {
      setCmdStatus('fail');
      setLastCmd({ command, error: 'Backend unreachable' });
    } finally {
      setLoading(false);
      // Auto-clear status after 3 s
      setTimeout(() => setCmdStatus(null), 3000);
    }
  }, [onCommandSent]);

  const handleStartStop = () => {
    const cmd = capturing ? 'STOP_CAPTURE' : 'START_CAPTURE';
    setCapturing(!capturing);
    onCommandSent && onCommandSent(cmd);   // fire immediately — no API needed
    sendCommand(cmd);                      // also try the backend (best-effort)
  };

  const handleIntervalChange = (newVal) => {
    setIntervalVal(newVal);
    sendCommand(`SET_INTERVAL:${newVal}`);
  };

  const handleModeToggle = () => {
    const newMode = mode === 'online' ? 'offline' : 'online';
    setMode(newMode);
    sendCommand(newMode === 'online' ? 'SWITCH_ONLINE' : 'SWITCH_OFFLINE');
  };

  return (
    <div
      className="flex items-center gap-3 px-4 py-2 flex-wrap"
      style={{
        background: 'rgba(8, 16, 26, 0.95)',
        borderTop: '1px solid rgba(45, 74, 110, 0.5)',
      }}
    >
      {/* Label */}
      <div className="flex items-center gap-1.5 mr-1">
        <span className="text-base">🚁</span>
        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
          Drone Controls
        </span>
      </div>

      {/* Divider */}
      <div className="w-px h-5 bg-slate-700" />

      {/* Start / Stop */}
      <button
        onClick={handleStartStop}
        disabled={loading}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold
                    transition-all active:scale-95 disabled:opacity-50 ${
          capturing
            ? 'bg-red-600 hover:bg-red-500 text-white'
            : 'bg-green-600 hover:bg-green-500 text-white'
        }`}
      >
        {loading ? (
          <span className="animate-spin text-xs">⟳</span>
        ) : (
          <span>{capturing ? '⏹' : '▶'}</span>
        )}
        {capturing ? 'Stop Capture' : 'Start Capture'}
      </button>

      {/* Capture interval */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-slate-400">Every</span>
        <div className="flex gap-0.5">
          {INTERVALS.map(({ label, value }) => (
            <button
              key={value}
              onClick={() => handleIntervalChange(value)}
              disabled={loading}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors
                         disabled:opacity-50 ${
                interval === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-5 bg-slate-700" />

      {/* Online / Offline toggle */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-400">Mode:</span>
        <button
          onClick={handleModeToggle}
          disabled={loading}
          className="relative flex items-center h-6 w-28 rounded-full p-0.5
                     transition-colors duration-300 focus:outline-none disabled:opacity-50"
          style={{
            background: mode === 'online'
              ? 'linear-gradient(135deg, #166534, #16a34a)'
              : 'linear-gradient(135deg, #1e3a5f, #2563eb)',
          }}
          title={mode === 'online' ? 'Online: images sent via MQTT' : 'Offline: saved to SD card'}
        >
          {/* Thumb */}
          <div
            className="absolute h-5 w-12 rounded-full bg-white shadow-md
                       transition-all duration-300 flex items-center justify-center"
            style={{ left: mode === 'online' ? '2px' : '50px' }}
          />
          <span
            className="relative z-10 text-xs font-bold text-white w-full text-center
                       pointer-events-none select-none"
            style={{ marginLeft: mode === 'online' ? '48px' : 0 }}
          >
            {mode === 'online' ? '📡 ONLINE' : '💾 OFFLINE'}
          </span>
        </button>
        <span className="text-xs text-slate-500">
          {mode === 'online' ? 'via MQTT' : 'SD card'}
        </span>
      </div>

      {/* Status indicator */}
      {cmdStatus && (
        <div
          className={`flex items-center gap-1 text-xs px-2 py-1 rounded fade-in ${
            cmdStatus === 'ok'
              ? 'bg-green-900/40 text-green-400'
              : 'bg-red-900/40 text-red-400'
          }`}
        >
          <span>{cmdStatus === 'ok' ? '✓' : '✗'}</span>
          <span>
            {cmdStatus === 'ok'
              ? `Sent: ${lastCmd.command}`
              : `Failed: ${lastCmd?.error}`}
          </span>
        </div>
      )}

      {/* Spacer */}
      <div className="flex-1" />

      {/* Quick status icons */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        {capturing && (
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-red-500 pulse-dot" />
            <span>REC</span>
          </div>
        )}
        <span>
          {mode === 'online'
            ? '📡 broker.hivemq.com:1883'
            : '💾 SD Card Mode'}
        </span>
      </div>
    </div>
  );
}
